import type { ColumnDef } from '@tanstack/react-table'
import type { CellRendererFn, ColumnSchema, DataTableTheme, Transaction, WorkspaceConfig } from '../types'
import { createElement } from 'react'
import { CellRenderer } from '../components/CellRenderer'
import { getDisplayText, getDisplayValue } from './display'
import { resolveColumnWidth } from './columnSizing'
import { resolveSlipMatchedAmount } from './incomeDerived'

function toEpochMs(value: unknown): number | null {
  const raw = getDisplayValue(value)
  if (raw == null) return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (raw instanceof Date) return Number.isFinite(raw.getTime()) ? raw.getTime() : null
  const text = String(raw).trim()
  if (!text) return null
  const ms = Date.parse(text)
  return Number.isFinite(ms) ? ms : null
}

function compareNullable(a: number | null, b: number | null) {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return a === b ? 0 : a < b ? -1 : 1
}

export function buildColumnDefs(
  cols: ColumnSchema[],
  workspace: WorkspaceConfig,
  customRenderers?: Partial<Record<string, CellRendererFn>>,
  searchQuery?: string | null,
  themeOverrides?: Partial<DataTableTheme>,
  workspaceId?: string,
): ColumnDef<Transaction, any>[] {
  return cols
    .filter(c => !c.configGate || c.configGate.includes(workspace.bill_lin_config))
    .filter(c => !c.scaleGate || c.scaleGate === workspace.scale)
    .filter(c => c.visibility !== 'hidden') // Filter out hidden completely from table headers
    .map(c => ({
      id: c.id,
      accessorFn: (row: Transaction) => {
        if (c.id === 'slip_matched_amount' || c.id === 'allocated_amount') {
          return resolveSlipMatchedAmount(row)
        }
        if (c.id === 'unallocated_amount') {
          const raw = getDisplayValue(row.unallocated_amount)
          const n = Number(raw)
          return Number.isFinite(n) ? n : 0
        }
        return row[c.id]
      },
      header: c.header,
      size: resolveColumnWidth(c),
      sortingFn: (() => {
        if (c.type === 'datetime' || c.type === 'date') {
          return (rowA, rowB, columnId) => {
            const a = toEpochMs(rowA.getValue(columnId))
            const b = toEpochMs(rowB.getValue(columnId))
            return compareNullable(a, b)
          }
        }
        if (c.type === 'amount') {
          return (rowA, rowB, columnId) => {
            const a = getDisplayValue(rowA.getValue(columnId))
            const b = getDisplayValue(rowB.getValue(columnId))
            const an = typeof a === 'number' ? a : Number(a)
            const bn = typeof b === 'number' ? b : Number(b)
            return compareNullable(Number.isFinite(an) ? an : null, Number.isFinite(bn) ? bn : null)
          }
        }
        // Fallback: compare display text so object cells sort deterministically.
        return (rowA, rowB, columnId) => {
          const a = getDisplayText(rowA.getValue(columnId))
          const b = getDisplayText(rowB.getValue(columnId))
          return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        }
      })(),
      meta: {
        type: c.type,
        editable: c.editable,
        sortable: c.sortable ?? false,
        align: c.align ?? (c.type === 'amount' ? 'right' : 'left'),
        // Keep parity with vendor dist: visibility classes are not applied in table cells.
        searchQuery,
      },
      cell: ({ row, getValue }) => {
        const value = getValue()
        if (customRenderers && customRenderers[c.id]) {
          return customRenderers[c.id]!({ value, record: row.original, col: c })
        }
        return createElement(CellRenderer, { col: c, value, record: row.original, searchQuery, themeOverrides, workspaceId })
      },
    }))
}
